import React from "react";
import {
  commitTaktEdits,
  fetchPullPlanTaktSections,
  previewTaktEdits,
  trackEvent,
} from "../../../api/clientV2";
import type {
  ExistingWorkCode,
  PullPlanTaktSectionRef,
  TaktEditSummary,
} from "../../../models";
import { colorForPrefix } from "../../../utils/color";
import StructureTab from "./StructureTab/StructureTab";
import WorkCodesTab from "./WorkCodesTab/WorkCodesTab";
import WbsTab from "./WbsTab/WbsTab";
import ConfirmCommitModal from "./ConfirmCommitModal/ConfirmCommitModal";
import {
  changeCount,
  createTradeDraft,
  deleteLevel,
  deleteTradeDraft,
  deleteZone,
  editActivities,
  editActivity,
  editWorkCode,
  emptyEdits,
  mergeWorkCodes,
  renameLevel,
  renameSection,
  renameZone,
  reorderLevels,
  reorderSections,
  reorderZones,
  setWorkCodeTrade,
  updateTradeDraft,
} from "./state";
import type {
  ActivityCommitted,
  ActivityMove,
  ActivityPatch,
  CodeEdit,
  TaktEdits,
} from "./state";
import "./TaktEditorModal.css";

type Tab = "wbs" | "structure" | "codes";

interface Props {
  scheduleId: number;
  /** Which tab opens first — e.g. the board's "Manage trades" entry opens
   *  straight onto Work Codes. */
  initialTab?: Tab;
  onClose: () => void;
  /** Called after a successful save — the host closes the editor + reloads the
   *  board in place (scroll preserved). */
  onSaved: () => void;
}

/** Board-wide Takt metadata editor. Edits everything Takt — work code values,
 *  names, colors, trades, section/zone/level labels, structure — in one place,
 *  never touching the activities table or predecessors. Edits stage in a local
 *  buffer; "Apply changes" previews a summary, then commits the whole batch on
 *  confirm. */
const TaktEditorModal: React.FC<Props> = ({
  scheduleId,
  initialTab,
  onClose,
  onSaved,
}) => {
  const [tab, setTab] = React.useState<Tab>(initialTab ?? "structure");
  const [sections, setSections] = React.useState<PullPlanTaktSectionRef[] | null>(null);
  const [edits, setEdits] = React.useState<TaktEdits>(emptyEdits);
  const undoStack = React.useRef<TaktEdits[]>([]);
  const redoStack = React.useRef<TaktEdits[]>([]);
  const [histTick, setHistTick] = React.useState(0);
  // Save flow: Apply → preview summary → confirm modal → commit → onSaved.
  const [summary, setSummary] = React.useState<TaktEditSummary | null>(null);
  const [preparing, setPreparing] = React.useState(false);
  const [committing, setCommitting] = React.useState(false);
  const [error, setError] = React.useState<string | null>(null);

  const reloadSections = React.useCallback(() => {
    fetchPullPlanTaktSections(scheduleId)
      .then((rows) => setSections(rows))
      .catch(() => {});
  }, [scheduleId]);

  // Saving WBS-node mappings changes the section structure + node defaults; bump
  // this so the Structure tab re-fetches its loaded sections (new mapped levels).
  const [wbsNonce, setWbsNonce] = React.useState(0);
  // A WBS-mapping save persists immediately (its own footer). If the user then
  // closes the editor, reload the board so the new structure is reflected.
  const wbsSaved = React.useRef(false);
  const onWbsSaved = React.useCallback(() => {
    wbsSaved.current = true;
    reloadSections();
    setWbsNonce((n) => n + 1);
  }, [reloadSections]);
  const handleClose = React.useCallback(() => {
    if (wbsSaved.current) onSaved();
    else onClose();
  }, [onSaved, onClose]);

  React.useEffect(() => {
    reloadSections();
  }, [reloadSections]);

  React.useEffect(() => {
    void trackEvent("takt_editor_opened", { tab: initialTab ?? "structure" });
  }, [initialTab]);

  // Every edit snapshots the prior buffer for undo and clears the redo branch.
  const apply = React.useCallback((producer: (e: TaktEdits) => TaktEdits) => {
    setEdits((prev) => {
      undoStack.current.push(prev);
      redoStack.current = [];
      return producer(prev);
    });
    setHistTick((t) => t + 1);
  }, []);

  const undo = React.useCallback(() => {
    const prev = undoStack.current.pop();
    if (prev === undefined) return;
    setEdits((cur) => {
      redoStack.current.push(cur);
      return prev;
    });
    setHistTick((t) => t + 1);
  }, []);

  const redo = React.useCallback(() => {
    const next = redoStack.current.pop();
    if (next === undefined) return;
    setEdits((cur) => {
      undoStack.current.push(cur);
      return next;
    });
    setHistTick((t) => t + 1);
  }, []);

  React.useEffect(() => {
    const handler = (e: KeyboardEvent) => {
      if (e.key === "Escape") {
        if (committing) return; // don't bail mid-save
        if (summary) {
          setSummary(null); // cancel the confirm step, keep the editor + edits
          setError(null);
          return;
        }
        handleClose();
        return;
      }
      const mod = e.metaKey || e.ctrlKey;
      if (mod && e.key === "z" && !e.shiftKey) {
        e.preventDefault();
        undo();
      } else if (mod && ((e.key === "z" && e.shiftKey) || e.key === "y")) {
        e.preventDefault();
        redo();
      }
    };
    window.addEventListener("keydown", handler);
    return () => window.removeEventListener("keydown", handler);
  }, [handleClose, undo, redo, summary, committing]);

  const count = changeCount(edits);

  // Codes created/assigned earlier this session — surfaced in the work-code
  // pickers so a code added in one section is reusable in another (it isn't on
  // the backend yet).
  const sessionCodes = React.useMemo<ExistingWorkCode[]>(() => {
    const map = new Map<string, ExistingWorkCode>();
    const put = (code?: string, name?: string, color?: string, override?: boolean) => {
      const c = (code ?? "").trim();
      if (!c) return;
      const prefix = (name ?? "").trim();
      const key = `${c}|${prefix}`;
      // A Work Codes tab edit (codeEdits) is authoritative; among per-activity
      // assignments the first one defines the pill (deterministic, not id-order).
      if (map.has(key) && !override) return;
      map.set(key, {
        code: c,
        prefix,
        // Fall back to the CODE's derived hue so the pill matches the board chip
        // (the board colors per Takt_Work; an unset color renders as
        // colorForPrefix of the code, not the prefix).
        color: (color || "").trim() || colorForPrefix(c),
        activity_count: 0,
        sections: [],
      });
    };
    for (const ae of Object.values(edits.activityEdits)) put(ae.code, ae.name, ae.color, false);
    for (const ce of Object.values(edits.codeEdits)) {
      if (ce.newCode) put(ce.newCode, ce.name, ce.color, true);
    }
    return Array.from(map.values());
  }, [edits.activityEdits, edits.codeEdits]);

  const handleApply = async () => {
    setPreparing(true);
    setError(null);
    try {
      const s = await previewTaktEdits(scheduleId, edits);
      setSummary(s);
    } catch {
      setError("Couldn't prepare the change summary. Please try again.");
    } finally {
      setPreparing(false);
    }
  };

  const handleConfirm = async () => {
    setCommitting(true);
    setError(null);
    try {
      await commitTaktEdits(scheduleId, edits);
      void trackEvent("takt_editor_saved");
      onSaved();
    } catch {
      setError("Save failed — no changes were applied. Please try again.");
      setCommitting(false);
    }
  };

  return (
    // Deliberately NOT click-out-closable: the editor can hold a long staged
    // session, and a text-selection drag that ends on the backdrop fires a
    // click there — closing would throw the edits away. Exits: ✕ / Cancel / Esc.
    <div className="takt-editor-overlay">
      <div
        className="takt-editor-modal"
        role="dialog"
        aria-modal="true"
        aria-label="Takt Editor"
      >
        <div className="takt-editor-header">
          <div className="takt-editor-tabs">
            <button
              type="button"
              className={`takt-editor-tab ${tab === "wbs" ? "is-active" : ""}`}
              onClick={() => setTab("wbs")}
            >
              <span className="takt-editor-tab-label">Takt Sections</span>
              <span className="takt-editor-tab-sub">Sections · WBS tree</span>
            </button>
            <button
              type="button"
              className={`takt-editor-tab ${tab === "structure" ? "is-active" : ""}`}
              onClick={() => setTab("structure")}
            >
              <span className="takt-editor-tab-label">Takt Structure</span>
              <span className="takt-editor-tab-sub">Activities · work codes</span>
            </button>
            <button
              type="button"
              className={`takt-editor-tab ${tab === "codes" ? "is-active" : ""}`}
              onClick={() => setTab("codes")}
            >
              <span className="takt-editor-tab-label">Takt Work Codes</span>
              <span className="takt-editor-tab-sub">Code · label · color · trade</span>
            </button>
          </div>
          <div className="takt-editor-header-right">
            {/* Undo/redo + the change counter drive the staged-edit buffer
                (Structure/Work Codes). The WBS tab saves immediately, so they'd
                be misleading there — a "0 changes" next to work that DID save
                reads as "nothing saved". */}
            {tab !== "wbs" && (
              <>
                <button
                  type="button"
                  className="takt-editor-hist"
                  onClick={undo}
                  disabled={undoStack.current.length === 0}
                  title="Undo"
                  data-tick={histTick}
                >
                  ↶
                </button>
                <button
                  type="button"
                  className="takt-editor-hist"
                  onClick={redo}
                  disabled={redoStack.current.length === 0}
                  title="Redo"
                >
                  ↷
                </button>
                <span className="takt-editor-changes" data-dirty={count > 0}>
                  {count} {count === 1 ? "change" : "changes"}
                </span>
              </>
            )}
            <button
              type="button"
              className="takt-editor-close"
              onClick={handleClose}
              aria-label="Close"
            >
              ✕
            </button>
          </div>
        </div>

        <div className="takt-editor-body">
          {tab === "wbs" ? (
            <WbsTab scheduleId={scheduleId} onSaved={onWbsSaved} />
          ) : sections === null ? (
            <div className="takt-editor-hint">Loading sections…</div>
          ) : tab === "structure" ? (
            <StructureTab
              scheduleId={scheduleId}
              sections={sections}
              edits={edits}
              sessionCodes={sessionCodes}
              onRenameSection={(orig, v) => apply((e) => renameSection(e, orig, v))}
              onRenameZone={(s, b, v) => apply((e) => renameZone(e, s, b, v))}
              onRenameLevel={(s, b, c, v) => apply((e) => renameLevel(e, s, b, c, v))}
              onApply={(id, patch: ActivityPatch, committed: ActivityCommitted) =>
                apply((e) => editActivity(e, id, patch, committed))
              }
              onApplyMany={(moves: ActivityMove[]) => apply((e) => editActivities(e, moves))}
              onDeleteZone={(s, b, moves: ActivityMove[]) => apply((e) => deleteZone(e, s, b, moves))}
              onDeleteLevel={(s, b, c, moves: ActivityMove[]) =>
                apply((e) => deleteLevel(e, s, b, c, moves))
              }
              onReorderSections={(order) => apply((e) => reorderSections(e, order))}
              onReorderZones={(section, order) => apply((e) => reorderZones(e, section, order))}
              onReorderLevels={(section, taktB, order) =>
                apply((e) => reorderLevels(e, section, taktB, order))
              }
              reloadKey={wbsNonce}
            />
          ) : (
            <WorkCodesTab
              scheduleId={scheduleId}
              edits={edits}
              onEditGroup={(sources, patch: CodeEdit) =>
                apply((e) => sources.reduce((acc, s) => editWorkCode(acc, s.code, s.prefix, patch), e))
              }
              onMergeWorkCodes={(sources, target, tradeEntries) =>
                apply((e) => {
                  const merged = mergeWorkCodes(e, sources, target);
                  return tradeEntries.length
                    ? setWorkCodeTrade(merged, tradeEntries)
                    : merged;
                })
              }
              onSetWorkCodeTrade={(entries) =>
                apply((e) => setWorkCodeTrade(e, entries))
              }
              onCreateTrade={(draft) => apply((e) => createTradeDraft(e, draft))}
              onUpdateTrade={(id, patch, current) =>
                apply((e) => updateTradeDraft(e, id, patch, current))
              }
              onDeleteTrade={(id) => apply((e) => deleteTradeDraft(e, id))}
            />
          )}
        </div>

        {/* The WBS tab owns its own staged Save/Discard footer; the shared
            footer is the staged-edit (Structure / Work Codes) commit. */}
        {tab !== "wbs" && (
          <div className="takt-editor-footer">
            <span className="takt-editor-footer-note">
              {error && !summary
                ? error
                : "Changes stay in this editor until applied."}
            </span>
            <button type="button" className="takt-editor-cancel" onClick={handleClose}>
              Cancel
            </button>
            <button
              type="button"
              className={`takt-editor-save ${count === 0 ? "is-empty" : ""}`}
              onClick={handleApply}
              disabled={count === 0 || preparing}
            >
              {preparing ? "Preparing…" : "Apply changes"}
            </button>
          </div>
        )}
      </div>

      {summary && (
        <ConfirmCommitModal
          summary={summary}
          committing={committing}
          error={error}
          onConfirm={handleConfirm}
          onCancel={() => {
            if (committing) return;
            setSummary(null);
            setError(null);
          }}
        />
      )}
    </div>
  );
};

export default TaktEditorModal;
